import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Clock, KeyRound, Loader2, CheckCircle2, Zap, RefreshCw, Wrench, Wallet } from "lucide-react";
import { connectAndMint, isMobileDevice, tokenPocketDeepLink } from "@/lib/caryConnect";
import CaryPactHistoryModal from "@/components/CaryPactHistoryModal";

const pad = (n) => String(n).padStart(2, "0");
const utcToLocal = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(); d.setUTCHours(h, m, 0, 0);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToUtc = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};
const fmtDate = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") + " UTC" : "never");

const Section = ({ icon, title, children }) => (
  <div className="rounded-xl p-4 mb-3" style={{ background: "rgba(15,18,38,0.03)", border: "1px solid rgba(15,18,38,0.06)" }}>
    <div className="flex items-center gap-2 mb-2.5">
      {icon}
      <span className="text-[12px] font-bold tracking-wide" style={{ color: "#0F1226" }}>{title}</span>
    </div>
    {children}
  </div>
);

/** One simple home for everything sync: daily time, token health, run-now. */
export default function SyncSettingsModal({ onClose, onSynced }) {
  const [st, setSt] = useState(null);
  const [localTime, setLocalTime] = useState("");
  const [savingTime, setSavingTime] = useState(false);
  const [token, setToken] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connErr, setConnErr] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const [runErr, setRunErr] = useState("");
  const [showConsole, setShowConsole] = useState(false);
  const pollRef = useRef(null);
  const doneRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/sync-settings");
      setSt(data);
      setLocalTime(utcToLocal(data.time_utc));
      if (data.running_job_id) startPoll(data.running_job_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []); // eslint-disable-line

  const startPoll = (jobId) => {
    clearInterval(pollRef.current);
    doneRef.current = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/carypact-sync-job/${jobId}`);
        setJob(data);
        if (["completed", "failed", "canceled", "paused_token"].includes(data.status) && !doneRef.current) {
          doneRef.current = true;
          clearInterval(pollRef.current);
          if (data.status === "completed") {
            toast.success(`Sync done — ${data.summary?.created || 0} new wallets added, ${data.summary?.updated || 0} updated`);
            onSynced?.();
          } else if (data.status === "paused_token") {
            setRunErr("Your CaryPact token expired mid-sync — reconnect above, then run again to resume.");
          } else if (data.status === "failed") {
            setRunErr(data.error || "Sync failed — try again.");
          }
        }
      } catch { /* transient — keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
  };

  const saveTime = async () => {
    if (!localTime) return;
    setSavingTime(true);
    try {
      const { data } = await api.post("/sync-settings/time", { time_utc: localToUtc(localTime) });
      setSt((s) => ({ ...s, time_utc: data.time_utc }));
      toast.success(`Daily sync moved to ${localTime} your time (${data.time_utc} UTC)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingTime(false); }
  };

  const saveToken = async () => {
    if (!token.trim()) return;
    setSavingToken(true);
    try {
      await api.post("/sync-settings/token", { token: token.trim() });
      setToken("");
      setRunErr("");
      toast.success("Fresh token saved — daily sync and one-tap syncs are back on");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingToken(false); }
  };

  // The proper way in: TokenPocket signs CaryPact's login challenge and the
  // server mints the token itself. No User Center, no devtools, no pasting.
  const connectWallet = async () => {
    setConnecting(true);
    setConnErr("");
    try {
      const data = await connectAndMint("Tree sync", st?.address || null);
      setRunErr("");
      if (data.mismatch) {
        toast.success(`Connected ${data.address.slice(0, 6)}…${data.address.slice(-4)} — that's the wallet open in TokenPocket, so sync is back on`, { duration: 8000 });
      } else {
        toast.success("Connected — daily sync and one-tap syncs are back on");
      }
      load();
    } catch (e) {
      setConnErr(e.response?.data?.detail || e.message || "Could not connect — try again.");
    } finally { setConnecting(false); }
  };

  const runNow = async () => {
    setRunErr("");
    setStarting(true);
    try {
      const { data } = await api.post("/sync-settings/run-now");
      startPoll(data.job_id);
    } catch (e) {
      if (e.response?.status === 409) setRunErr("Your CaryPact token has expired — reconnect your wallet above first.");
      else setRunErr(formatApiError(e));
    } finally { setStarting(false); }
  };

  const running = job && job.status === "running";
  const tokenOk = st && st.configured && !st.token_expired;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(10,12,28,0.55)" }} data-testid="sync-settings-modal">
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid rgba(15,18,38,0.1)" }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.08)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(64,96,255,0.12)" }}><RefreshCw size={15} style={{ color: "#4060FF" }} /></div>
            <div>
              <div className="font-semibold text-[15px]" style={{ color: "#0F1226" }}>Sync settings</div>
              <div className="text-[11px]" style={{ color: "#64748B" }}>Daily schedule · connection · full sync</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="sync-settings-close"><X size={16} /></button>
        </div>

        <div className="px-5 py-4">
          {!st ? (
            <div className="py-10 text-center text-sm" style={{ color: "#94A3B8" }} data-testid="sync-settings-loading"><Loader2 size={18} className="animate-spin mx-auto" /></div>
          ) : (
            <>
              <Section icon={<Clock size={13} style={{ color: "#4060FF" }} />} title="DAILY AUTO-SYNC">
                <div className="flex items-center gap-2">
                  <input type="time" value={localTime} onChange={(e) => setLocalTime(e.target.value)}
                    className="input-glass !py-2 text-sm font-mono" style={{ width: 120 }} data-testid="sync-time-input" />
                  <button onClick={saveTime} disabled={savingTime || utcToLocal(st.time_utc) === localTime}
                    className="btn-secondary !py-2 text-[12px] disabled:opacity-40" data-testid="sync-time-save">
                    {savingTime ? <Loader2 size={13} className="animate-spin" /> : "Save"}
                  </button>
                </div>
                <div className="text-[11px] mt-2" style={{ color: "#64748B" }}>
                  Runs every day at <b>{localTime || "—"}</b> your time ({st.time_utc} UTC) — refreshes the whole tree (new wallets included) and imports income for every connected wallet.
                </div>
                <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }} data-testid="sync-last-run">
                  Last run: {fmtDate(st.last_run_at)}{st.last_status ? ` · ${st.last_status === "ok" ? "OK" : st.last_status}` : ""}
                </div>
              </Section>

              <Section icon={<KeyRound size={13} style={{ color: tokenOk ? "#00863A" : "#B45309" }} />} title="CARYPACT CONNECTION">
                <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                  <span className="chip text-[11px]" data-testid="sync-token-status"
                    style={tokenOk ? { background: "rgba(0,134,58,0.12)", color: "#00863A", fontWeight: 800 }
                      : { background: "rgba(245,158,11,0.14)", color: "#B45309", fontWeight: 800 }}>
                    {tokenOk ? "Active" : st.configured ? "Expired — reconnect" : "Not connected yet"}
                  </span>
                  {st.token_saved_at && (
                    <span className="text-[10.5px]" style={{ color: "#94A3B8" }} data-testid="sync-token-meta">
                      {tokenOk && st.token_source ? `via ${st.token_source} · ` : ""}saved {fmtDate(st.token_saved_at)}
                    </span>
                  )}
                </div>

                <button onClick={connectWallet} disabled={connecting}
                  className="btn-primary w-full justify-center !py-2.5" data-testid="sync-token-connect">
                  {connecting ? <><Loader2 size={14} className="animate-spin" /> Waiting for TokenPocket…</>
                    : <><Wallet size={14} /> {tokenOk ? "Reconnect wallet" : "Connect with TokenPocket"}</>}
                </button>
                <div className="text-[10.5px] mt-1.5" style={{ color: "#94A3B8" }}>
                  TokenPocket signs a one-line message and we fetch the token ourselves — nothing to copy.
                  {st.address ? <> Use <b>{st.address.slice(0, 6)}…{st.address.slice(-4)}</b> if you can.</> : null}
                </div>
                {connErr && <div className="chip chip-amber mt-2 w-full text-[11px]" data-testid="sync-token-connect-error">{connErr}</div>}
                {connErr && isMobileDevice() && (
                  <a href={tokenPocketDeepLink()} className="btn-secondary w-full justify-center !py-2 text-[12px] mt-2"
                    data-testid="sync-token-open-tokenpocket">Open this page in TokenPocket</a>
                )}

                {showPaste ? (
                  <div className="mt-3 pt-3" style={{ borderTop: "1px dashed rgba(15,18,38,0.12)" }}>
                    <div className="flex items-center gap-2">
                      <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste a CaryPact token…"
                        className="input-glass !py-2 text-[12px] font-mono flex-1" data-testid="sync-token-input" />
                      <button onClick={saveToken} disabled={savingToken || !token.trim()}
                        className="btn-secondary !py-2 text-[12px] disabled:opacity-40" data-testid="sync-token-save">
                        {savingToken ? <Loader2 size={13} className="animate-spin" /> : "Save"}
                      </button>
                    </div>
                    <div className="text-[10.5px] mt-1.5" style={{ color: "#94A3B8" }}>
                      Only needed if TokenPocket isn&apos;t on this device: CaryPact User Center → copy your session token.
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setShowPaste(true)}
                    className="text-[11px] font-bold mt-2 hover:underline" style={{ color: "#5A607F" }}
                    data-testid="sync-token-paste-toggle">
                    No wallet on this device? Paste a token instead →
                  </button>
                )}
              </Section>

              <Section icon={<Zap size={13} style={{ color: "#00863A" }} />} title="FULL SYNC NOW">
                {running ? (
                  <div data-testid="sync-run-status">
                    <div className="flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: "#0F1226" }}>
                      <Loader2 size={14} className="animate-spin" style={{ color: "#4060FF" }} /> {job.phase || "Working…"}
                    </div>
                    <div className="text-[11px] mt-1.5 font-mono" style={{ color: "#64748B" }}>
                      {job.fetched || 0} walked · {job.created || 0} new · {job.updated || 0} updated · {job.queued || 0} queued
                    </div>
                  </div>
                ) : (
                  <>
                    <button onClick={runNow} disabled={starting} className="btn-primary w-full justify-center !py-2.5" data-testid="sync-run-now">
                      {starting ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <><Zap size={14} /> Run full sync now</>}
                    </button>
                    {job?.status === "completed" && (
                      <div className="flex items-center gap-1.5 text-[11.5px] mt-2 font-semibold" style={{ color: "#00863A" }} data-testid="sync-run-done">
                        <CheckCircle2 size={13} /> Done — {job.summary?.created || 0} new wallets, {job.summary?.updated || 0} updated
                      </div>
                    )}
                  </>
                )}
                {runErr && <div className="chip chip-amber mt-2.5 w-full text-[11px]" data-testid="sync-run-error">{runErr}</div>}
                <div className="text-[10.5px] mt-2" style={{ color: "#94A3B8" }}>
                  Walks your entire CaryPact downline and adds newly joined wallets to the org chart right away.
                </div>
              </Section>

              <button onClick={() => setShowConsole(true)}
                className="w-full flex items-center justify-center gap-1.5 text-[11.5px] font-bold py-2 rounded-xl hover:bg-black/[0.03] transition-colors"
                style={{ color: "#5A607F" }} data-testid="sync-advanced-console-btn">
                <Wrench size={12} /> Advanced: income import console →
              </button>
            </>
          )}
        </div>
      </div>
      {showConsole && (
        <CaryPactHistoryModal onClose={() => setShowConsole(false)} onImported={() => onSynced?.()} />
      )}
    </div>,
    document.body
  );
}
